/* ==========================================================================
   custom.js — shared utilities + global components for the custom theme layer.

   1. window.Custom — helpers reused by section scripts:
      createScope, escapeHtml, formatMoney, frameThrottle, addToCart
   2. Quick-add component — drives the bag buttons on product cards and the
      Choose-options drawer rendered by sections/custom-quick-add.liquid.

   Config comes from data attributes and window.routes / window.themeMoneyFormat
   (set in layout/theme.liquid) — no Liquid in this file, so it stays cacheable.
   ========================================================================== */

window.Custom = (function () {
  'use strict';

  /* listener registry with one-call cleanup — used by section lifecycles */
  function createScope() {
    var cleanupFns = [];
    return {
      on: function (target, event, handler, opts) {
        target.addEventListener(event, handler, opts);
        cleanupFns.push(function () {
          target.removeEventListener(event, handler, opts);
        });
      },
      add: function (fn) {
        cleanupFns.push(fn);
      },
      cleanup: function () {
        cleanupFns.forEach(function (fn) {
          fn();
        });
        cleanupFns = [];
      }
    };
  }

  function escapeHtml(value) {
    var div = document.createElement('div');
    div.textContent = value == null ? '' : value;
    return div.innerHTML;
  }

  /* one-per-frame scheduler with a timeout backstop so scroll-driven UI
     still syncs when rAF is throttled */
  function frameThrottle(fn) {
    var pending = false;
    return function () {
      if (pending) return;
      pending = true;
      var done = false;
      var run = function () {
        if (done) return;
        done = true;
        pending = false;
        fn();
      };
      if (window.requestAnimationFrame) requestAnimationFrame(run);
      setTimeout(run, 120);
    };
  }

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    var parts = (cents / 100).toFixed(precision).split('.');
    var units = parts[0].replace(/(\d)(?=(\d{3})+$)/g, '$1' + thousands);
    return units + (parts[1] ? decimal + parts[1] : '');
  }

  function formatMoney(cents) {
    var format = window.themeMoneyFormat || '${{amount}}';
    var match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var type = match ? match[1] : 'amount';
    var value;
    switch (type) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2, ',', '.');
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  function cartDrawer() {
    return document.querySelector('cart-drawer');
  }

  /* AJAX add-to-cart wired to the theme cart drawer */
  function addToCart(variantId, onDone, onError) {
    var routes = window.routes || {};
    var body = { id: variantId, quantity: 1 };
    var cd = cartDrawer();
    if (cd && cd.getSectionsToRender) {
      body.sections = cd.getSectionsToRender().map(function (s) {
        return s.id;
      });
      body.sections_url = window.location.pathname;
    }
    fetch(routes.cart_add_url || '/cart/add', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (res) {
        if (res.status) {
          if (onError) onError(res.description || res.message || 'Error');
          return;
        }
        if (cd && cd.renderContents) cd.renderContents(res);
        else window.location.href = routes.cart_url || '/cart';
        if (onDone) onDone();
      })
      .catch(function () {
        if (onError) onError('Error');
      });
  }

  return {
    createScope: createScope,
    escapeHtml: escapeHtml,
    frameThrottle: frameThrottle,
    formatMoney: formatMoney,
    addToCart: addToCart
  };
})();

/* ==========================================================================
   Quick add — card bag buttons + Choose-options drawer (#CustomQuickAdd)
   ========================================================================== */
(function () {
  'use strict';

  var scope = null;

  var SPINNER =
    '<div class="loading__spinner hidden"><svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></div>';

  function init() {
    var drawer = document.getElementById('CustomQuickAdd');
    if (!drawer) return;
    scope = window.Custom.createScope();
    var on = scope.on;
    var esc = window.Custom.escapeHtml;
    var money = window.Custom.formatMoney;

    var mediaEl = drawer.querySelector('[data-qa-media]');
    var infoEl = drawer.querySelector('[data-qa-info]');
    var STR = {
      add: drawer.getAttribute('data-str-add') || 'Add to cart',
      soldOut: drawer.getAttribute('data-str-sold-out') || 'Sold out',
      unavailable: drawer.getAttribute('data-str-unavailable') || 'Unavailable',
      viewDetails: drawer.getAttribute('data-str-details') || 'View full details'
    };
    var rootUrl = drawer.getAttribute('data-root-url') || '/';
    var productBase = (rootUrl === '/' ? '' : rootUrl) + '/products/';

    var product = null;
    var lastTrigger = null;
    var productCache = {};
    var openToken = 0;

    function openDrawer() {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
      /* the drawer was off-screen when the slider was built, so re-measure now
         that it has real dimensions */
      requestAnimationFrame(function () {
        if (mediaEmbla) mediaEmbla.reInit();
      });
    }

    function closeDrawer() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
      if (window.Custom && window.Custom.hideCloseCursor) window.Custom.hideCloseCursor();
      if (lastTrigger) lastTrigger.focus();
    }

    drawer.querySelectorAll('[data-qa-close]').forEach(function (el) {
      on(el, 'click', closeDrawer);
    });
    on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
    });

    /* ---- delegated once — the info panel's innerHTML is rebuilt per product,
       so per-open listeners would stack across opens ---- */
    on(infoEl, 'change', function (e) {
      if (product && e.target.closest('[data-qa-opt]')) applyVariant(true);
    });

    on(infoEl, 'click', function (e) {
      var atc = e.target.closest('[data-qa-atc]');
      if (!atc || !product) return;
      var single = product.variants.length === 1;
      var variant = single ? product.variants[0] : findVariant(selectedValues());
      if (!variant || !variant.available) return;
      atc.classList.add('is-loading');
      var sp = atc.querySelector('.loading__spinner');
      if (sp) sp.classList.remove('hidden');
      window.Custom.addToCart(
        variant.id,
        function () {
          atc.classList.remove('is-loading');
          if (sp) sp.classList.add('hidden');
          closeDrawer();
        },
        function (msg) {
          atc.classList.remove('is-loading');
          if (sp) sp.classList.add('hidden');
          showError(msg);
        }
      );
    });

    /* dot syncing is driven by Embla's "select" event (see buildMediaSlider) */

    /* ---- option helpers ---- */
    function optionNames() {
      return (product.options || []).map(function (o) {
        return typeof o === 'string' ? o : o.name;
      });
    }
    function optionValues(i) {
      var seen = {};
      var vals = [];
      product.variants.forEach(function (v) {
        var val = v.options[i];
        if (val != null && !seen[val]) {
          seen[val] = 1;
          vals.push(val);
        }
      });
      return vals;
    }
    function selectedValues() {
      var vals = [];
      infoEl.querySelectorAll('[data-qa-opt]').forEach(function (input) {
        if (input.checked) vals[Number(input.getAttribute('data-qa-opt'))] = input.value;
      });
      return vals;
    }
    function findVariant(values) {
      return (
        product.variants.find(function (v) {
          return values.every(function (val, i) {
            return v.options[i] === val;
          });
        }) || null
      );
    }

    function withWidth(url, w) {
      if (!url) return '';
      return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
    }

    /* ---- build the gallery ---- */
    function buildMedia() {
      var items =
        product.media && product.media.length
          ? product.media
              .filter(function (m) {
                return m.media_type === 'image' || m.preview_image;
              })
              .map(function (m) {
                return { src: (m.preview_image && m.preview_image.src) || m.src, id: m.id };
              })
          : (product.images || []).map(function (src) {
              return { src: src, id: null };
            });
      if (!items.length && product.featured_image) {
        items = [{ src: product.featured_image, id: null }];
      }

      var slides = items
        .map(function (item) {
          return (
            '<div class="qa-media__slide" data-qa-slide data-media-id="' +
            (item.id || '') +
            '"><img src="' +
            esc(withWidth(item.src, 900)) +
            '" alt="" loading="lazy"></div>'
          );
        })
        .join('');

      var dots =
        items.length > 1
          ? '<div class="qa-media__dots">' +
            items
              .map(function (_, i) {
                /* a real button so it is clickable and keyboard reachable */
                return (
                  '<button type="button" class="qa-media__dot' +
                  (i === 0 ? ' is-active' : '') +
                  '" data-qa-dot aria-label="' +
                  (i + 1) +
                  '"></button>'
                );
              })
              .join('') +
            '</div>'
          : '';

      /* Embla needs viewport > container > slides (mobile only — desktop keeps
         the vertical native-scroll column) */
      mediaEl.innerHTML =
        '<div class="qa-media__viewport" data-qa-viewport>' +
        '<div class="qa-media__track" data-qa-track>' +
        slides +
        '</div></div>' +
        dots;
      var track = mediaEl.querySelector('[data-qa-track]');
      if (track && track.scrollTo) track.scrollTo(0, 0);
      buildMediaSlider();
    }

    /* ---- mobile media slider (Embla, shared factory) ------------------- */
    var mediaEmbla = null;
    var mediaMobile = window.matchMedia('(max-width: 749px)');

    function destroyMediaSlider() {
      if (mediaEmbla) mediaEmbla.destroy();
      mediaEmbla = null;
    }

    function buildMediaSlider() {
      destroyMediaSlider();
      if (!mediaMobile.matches) return;
      var viewport = mediaEl.querySelector('[data-qa-viewport]');
      if (!viewport) return;
      var slideEls = mediaEl.querySelectorAll('[data-qa-slide]');
      if (slideEls.length < 2) return;
      if (!window.Custom || !window.Custom.createEmbla) return;

      mediaEmbla = window.Custom.createEmbla(viewport, {
        align: 'start',
        containScroll: 'trimSnaps',
        loop: false
      });
      if (!mediaEmbla) return;

      function syncDots() {
        var idx = mediaEmbla.selectedScrollSnap();
        mediaEl.querySelectorAll('.qa-media__dot').forEach(function (d, i) {
          d.classList.toggle('is-active', i === idx);
        });
      }
      mediaEmbla.on('select', syncDots);
      mediaEmbla.on('reInit', syncDots);
      syncDots();

      /* clicking a dot jumps to that image (the dots had no handler at all
         before, so they were decoration only) */
      var dotsWrap = mediaEl.querySelector('.qa-media__dots');
      if (dotsWrap && !dotsWrap.hasAttribute('data-qa-dots-bound')) {
        dotsWrap.setAttribute('data-qa-dots-bound', '');
        dotsWrap.addEventListener('click', function (event) {
          var dot = event.target.closest('[data-qa-dot]');
          if (!dot || !mediaEmbla) return;
          var all = mediaEl.querySelectorAll('[data-qa-dot]');
          mediaEmbla.scrollTo(Array.prototype.indexOf.call(all, dot));
        });
      }
    }

    /* scroll the gallery to a variant's image — vertical column on desktop,
       horizontal carousel on mobile (the dot sync follows the scroll event) */
    function scrollToMedia(mediaId) {
      if (!mediaId) return;
      var track = mediaEl.querySelector('[data-qa-track]');
      if (!track) return;
      var slides = track.querySelectorAll('[data-qa-slide]');
      for (var i = 0; i < slides.length; i++) {
        if (slides[i].getAttribute('data-media-id') === String(mediaId)) {
          /* mobile is an Embla carousel; desktop is still a native vertical
             scroll column, so each needs its own way of getting there */
          if (mediaEmbla) mediaEmbla.scrollTo(i);
          else track.scrollTo({ left: slides[i].offsetLeft, top: slides[i].offsetTop, behavior: 'smooth' });
          break;
        }
      }
    }

    /* ---- build the info panel ---- */
    function buildInfo() {
      var names = optionNames();
      var single = product.variants.length === 1;

      var preselected =
        product.variants.find(function (v) {
          return v.available;
        }) || product.variants[0];
      var optionsHtml = single
        ? ''
        : names
            .map(function (name, i) {
              var currentVal = preselected.options[i];
              var pills = optionValues(i)
                .map(function (val) {
                  var checked = val === currentVal ? ' checked' : '';
                  return (
                    '<label class="qa-pill" data-qa-pill data-opt-index="' +
                    i +
                    '" data-opt-value="' +
                    esc(val) +
                    '">' +
                    '<input type="radio" name="qa-option-' +
                    i +
                    '" value="' +
                    esc(val) +
                    '" data-qa-opt="' +
                    i +
                    '"' +
                    checked +
                    '>' +
                    '<span>' +
                    esc(val) +
                    '</span></label>'
                  );
                })
                .join('');
              return (
                '<fieldset class="qa-option"><legend class="qa-option__label">' +
                esc(name) +
                '</legend><div class="qa-pills">' +
                pills +
                '</div></fieldset>'
              );
            })
            .join('');

      infoEl.innerHTML =
        (product.vendor
          ? '<a class="qa-info__vendor" href="' + esc(product.url) + '">' + esc(product.vendor) + '</a>'
          : '') +
        '<h2 class="qa-info__title"><a href="' +
        esc(product.url) +
        '">' +
        esc(product.title) +
        '</a></h2>' +
        '<div class="qa-price" data-qa-price></div>' +
        '<p class="qa-info__error" data-qa-error hidden></p>' +
        optionsHtml +
        '<button class="qa-atc" type="button" data-qa-atc><span></span>' +
        SPINNER +
        '</button>' +
        '<a class="qa-info__details" href="' +
        esc(product.url) +
        '">' +
        esc(STR.viewDetails) +
        '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
        '<path d="M4 12H20M20 12L14 6M20 12L14 18" stroke="currentColor" stroke-width="1.5"/></svg></a>';

      applyVariant();
    }

    function showError(msg) {
      var el = infoEl.querySelector('[data-qa-error]');
      if (!el) return;
      el.textContent = msg;
      el.hidden = false;
    }

    function applyVariant(jumpMedia) {
      var single = product.variants.length === 1;
      var variant = single ? product.variants[0] : findVariant(selectedValues());

      // e.g. picking a color scrolls the gallery to that variant's image;
      // options without their own media (sizes) simply don't scroll
      if (jumpMedia && variant && variant.featured_media) {
        scrollToMedia(variant.featured_media.id);
      }

      if (!single) {
        var values = selectedValues();
        infoEl.querySelectorAll('[data-qa-pill]').forEach(function (pill) {
          var idx = Number(pill.getAttribute('data-opt-index'));
          var candidate = values.slice();
          candidate[idx] = pill.getAttribute('data-opt-value');
          var v = findVariant(candidate);
          pill.classList.toggle('is-unavailable', !v || !v.available);
        });
      }

      var priceEl = infoEl.querySelector('[data-qa-price]');
      if (priceEl) {
        if (variant) {
          var html = '<span class="qa-price__current">' + money(variant.price) + '</span>';
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            html += '<s class="qa-price__compare">' + money(variant.compare_at_price) + '</s>';
            var pct = Math.round(
              ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100
            );
            html += '<span class="qa-price__badge">-' + pct + '%</span>';
          }
          priceEl.innerHTML = html;
        } else {
          priceEl.innerHTML = '';
        }
      }

      var atc = infoEl.querySelector('[data-qa-atc]');
      if (atc) {
        var label = STR.add;
        var available = !!(variant && variant.available);
        if (!variant) label = STR.unavailable;
        else if (!available) label = STR.soldOut;
        var span = atc.querySelector('span');
        if (span) span.textContent = label;
        atc.disabled = !available;
      }

      var err = infoEl.querySelector('[data-qa-error]');
      if (err) err.hidden = true;
    }

    /* ---- open for a given handle ---- */
    function loadProduct(handle) {
      if (productCache[handle]) return Promise.resolve(productCache[handle]);
      return fetch(productBase + handle + '.js')
        .then(function (r) {
          return r.json();
        })
        .then(function (data) {
          productCache[handle] = data;
          return data;
        });
    }

    function openForHandle(handle, trigger) {
      lastTrigger = trigger || null;
      var token = ++openToken; // stale responses must not overwrite a newer open
      infoEl.innerHTML = '<div class="qa-drawer__loader">' + SPINNER.replace('hidden', '') + '</div>';
      mediaEl.innerHTML = '';
      openDrawer();

      loadProduct(handle)
        .then(function (data) {
          if (token !== openToken) return;
          product = data;
          buildMedia();
          buildInfo();
        })
        .catch(function () {
          if (token !== openToken) return;
          infoEl.innerHTML = '<p class="qa-info__error">' + esc(STR.unavailable) + '</p>';
        });
    }

    /* ---- delegated card button clicks ---- */
    on(document, 'click', function (e) {
      var btn = e.target.closest('[data-quick-add]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();

      var variantId = btn.getAttribute('data-quick-add-variant');
      var handle = btn.getAttribute('data-quick-add-handle');
      var auto = btn.getAttribute('data-quick-add-auto');

      if (variantId) {
        // single-variant product: straight to cart
        btn.classList.add('is-loading');
        window.Custom.addToCart(
          Number(variantId),
          function () {
            btn.classList.remove('is-loading');
          },
          function () {
            btn.classList.remove('is-loading');
          }
        );
      } else if (handle) {
        openForHandle(handle, btn);
      } else if (auto) {
        // variant count unknown (e.g. search results): fetch first, then
        // apply the same rule as regular cards
        btn.classList.add('is-loading');
        loadProduct(auto)
          .then(function (data) {
            if (data.variants && data.variants.length === 1) {
              window.Custom.addToCart(
                data.variants[0].id,
                function () {
                  btn.classList.remove('is-loading');
                },
                function () {
                  btn.classList.remove('is-loading');
                }
              );
            } else {
              btn.classList.remove('is-loading');
              openForHandle(auto, btn); // cached — opens instantly
            }
          })
          .catch(function () {
            btn.classList.remove('is-loading');
          });
      }
    });
  }

  function destroy() {
    document.documentElement.style.overflow = '';
    if (scope) {
      scope.cleanup();
      scope = null;
    }
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target.querySelector('#CustomQuickAdd')) {
      destroy();
      init();
    }
  });
  document.addEventListener('shopify:section:unload', function (e) {
    if (e.target.querySelector('#CustomQuickAdd')) destroy();
  });
})();

/* ==========================================================================
   Product card media slider (snippets/card-media-slider.liquid)

   Uses Embla (assets/embla-carousel.js, self-hosted — no CDN request) for the
   same drag feel as the reference store. Also exposes window.Custom.createEmbla
   so the product gallery and the cart-drawer upsell can share one library.

   Three guards keep this cheap on a page with 40+ cards:
   1. instances are created lazily via IntersectionObserver, so off-screen cards
      cost nothing until they scroll into view;
   2. mobile only — above 749px the slides are stacked by CSS and no instance is
      created at all;
   3. crossing the breakpoint destroys or builds instances rather than leaving
      dead ones running.
   ========================================================================== */
(function () {
  var READY = 'data-cu-slider-ready';
  var mobile = window.matchMedia('(max-width: 749px)');
  var instances = [];

  /* shared helper — other components create their own carousels through this
     so there is exactly one Embla in the theme */
  function createEmbla(viewport, options) {
    if (!viewport || typeof window.EmblaCarousel !== 'function') return null;
    return window.EmblaCarousel(viewport, options || {});
  }

  function activate(slider) {
    if (slider.hasAttribute(READY)) return;

    var viewport = slider.querySelector('[data-cu-viewport]');
    var dots = slider.querySelectorAll('[data-cu-dot]');
    if (!viewport || dots.length < 2) return;

    var embla = createEmbla(viewport, {
      align: 'start',
      containScroll: 'trimSnaps',
      loop: false,
      dragFree: false
    });
    if (!embla) return;

    slider.setAttribute(READY, '');

    function syncDots() {
      var index = embla.selectedScrollSnap();
      for (var i = 0; i < dots.length; i++) {
        dots[i].classList.toggle('is-active', i === index);
      }
    }

    embla.on('select', syncDots);
    embla.on('reInit', syncDots);
    syncDots();

    var dotsWrap = slider.querySelector('[data-cu-dots]');
    if (dotsWrap) {
      dotsWrap.addEventListener('click', function (event) {
        var dot = event.target.closest('[data-cu-dot]');
        if (!dot) return;
        /* slides are links — stop the tap from opening the product */
        event.preventDefault();
        event.stopPropagation();
        embla.scrollTo(Array.prototype.indexOf.call(dots, dot));
      });
    }

    instances.push({ slider: slider, embla: embla });
  }

  function destroyAll() {
    instances.forEach(function (entry) {
      entry.embla.destroy();
      entry.slider.removeAttribute(READY);
    });
    instances = [];
  }

  var observer = null;
  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          activate(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '200px' }
    );
  }

  function initCardSliders(scope) {
    if (!mobile.matches) return;
    var root = scope || document;
    var sliders = root.querySelectorAll('[data-cu-cardslider]:not([' + READY + '])');
    Array.prototype.forEach.call(sliders, function (slider) {
      if (observer) observer.observe(slider);
      else activate(slider);
    });
  }

  if (window.Custom) {
    /* exposed so sections can re-init after appending cards (e.g. Load more) */
    window.Custom.initCardSliders = initCardSliders;
    window.Custom.createEmbla = createEmbla;
  }

  function onBreakpointChange() {
    if (mobile.matches) initCardSliders();
    else destroyAll();
  }

  if (mobile.addEventListener) mobile.addEventListener('change', onBreakpointChange);
  else if (mobile.addListener) mobile.addListener(onBreakpointChange);

  document.addEventListener('DOMContentLoaded', function () {
    initCardSliders();
  });
  initCardSliders();

  document.addEventListener('shopify:section:load', function (event) {
    initCardSliders(event.target);
  });

  /* Self-healing: cards get injected by several things that are not section
     loads — "Load more", the recommendations fetch, and Dawn's facets
     re-rendering the grid on filter/sort. Each of those would otherwise need to
     remember to call initCardSliders, and a missed one leaves dead sliders.
     Watching for added slider nodes covers all of them, including future ones.

     Kept cheap: the callback only runs when DOM is added, exits immediately if
     no slider is among the added nodes, and the real work is rAF-batched. */
  if ('MutationObserver' in window) {
    var scanQueued = false;
    new MutationObserver(function (mutations) {
      if (scanQueued || !mobile.matches) return;
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches('[data-cu-cardslider]') || node.querySelector('[data-cu-cardslider]')) {
            scanQueued = true;
            requestAnimationFrame(function () {
              scanQueued = false;
              initCardSliders();
            });
            return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }
})();

/* ==========================================================================
   Shared "close" cursor for drawer backdrops.

   A white X circle follows the pointer over any overlay carrying
   `data-close-cursor`, making it obvious the backdrop is clickable. One
   element, one listener, shared by the quick-add, cart and search drawers —
   adding it to a new drawer is just the attribute.

   Pointer devices only: on touch there is no hover state, so the element is
   never created at all.
   ========================================================================== */
(function () {
  if (!window.matchMedia || !matchMedia('(hover: hover)').matches) return;

  var HALF = 26; // half of the 5.2rem circle, to centre it on the pointer
  var el = null;

  function element() {
    if (el) return el;
    el = document.createElement('div');
    el.className = 'close-cursor';
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML =
      '<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<path d="M22.66 9.33L9.33 22.66M22.66 22.66L9.33 9.33" stroke="currentColor" stroke-width="1.5"/></svg>';
    document.body.appendChild(el);
    return el;
  }

  function hide() {
    if (el) el.classList.remove('is-visible');
  }

  /* delegated on document: drawer markup is replaced wholesale on cart updates,
     so per-overlay listeners would be lost on every re-render */
  document.addEventListener(
    'mousemove',
    function (event) {
      var overlay = event.target.closest && event.target.closest('[data-close-cursor]');
      if (!overlay) {
        hide();
        return;
      }
      var cursor = element();
      cursor.style.transform =
        'translate(' + (event.clientX - HALF) + 'px, ' + (event.clientY - HALF) + 'px)';
      cursor.classList.add('is-visible');
    },
    { passive: true }
  );

  /* clicking a backdrop closes its drawer and the pointer may never move
     again, so drop the cursor immediately rather than leaving it stranded */
  document.addEventListener('click', function (event) {
    if (event.target.closest && event.target.closest('[data-close-cursor]')) hide();
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') hide();
  });

  if (window.Custom) window.Custom.hideCloseCursor = hide;
})();
